import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState, useEffect } from "react";
import { Search, FilterX, CalendarIcon } from "lucide-react";
import { getSelectSellers } from "@/Api/sellers";
import { selectUsers } from "@/Api/user";
import { getLeadTrackingMedias, LeadTrackingFilters } from "@/Api/leadTracking";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface FiltersProps {
  onFilter: (filters: LeadTrackingFilters) => void;
  loading?: boolean;
}

interface SelectOption {
  id: number;
  name: string;
}

export const LeadTrackingFiltersComponent = ({ onFilter, loading }: FiltersProps) => {
  const [sellers, setSellers] = useState<SelectOption[]>([]);
  const [medias, setMedias] = useState<SelectOption[]>([]);
  const [users, setUsers] = useState<SelectOption[]>([]);

  const [sellerId, setSellerId] = useState<string>("");
  const [mediaId, setMediaId] = useState<string>("");
  const [createdById, setCreatedById] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    const fetchSelects = async () => {
      try {
        const [sellersRes, usersRes, mediasRes] = await Promise.all([
          getSelectSellers({}),
          selectUsers(),
          getLeadTrackingMedias(),
        ]);

        if (sellersRes.ok && sellersRes.data?.response) {
          setSellers(
            sellersRes.data.response.map((s: any) => ({
              id: s.id,
              name: s.user?.name || s.name,
            }))
          );
        }

        if (usersRes.ok && usersRes.data?.response) {
          setUsers(
            usersRes.data.response.map((u: any) => ({
              id: u.id,
              name: u.name,
            }))
          );
        }

        if (mediasRes.ok && mediasRes.data?.response) {
          setMedias(mediasRes.data.response);
        }
      } catch (error) {
        console.error("Error fetching selects:", error);
      }
    };

    fetchSelects();
  }, []);

  const handleFilter = () => {
    const filters: LeadTrackingFilters = {};

    if (sellerId && sellerId !== "all") filters.seller_id = parseInt(sellerId);
    if (mediaId && mediaId !== "all") filters.media_id = parseInt(mediaId);
    if (createdById && createdById !== "all") filters.created_by_id = parseInt(createdById);
    if (startDate) filters.start_date = format(startDate, "yyyy-MM-dd");
    if (endDate) filters.end_date = format(endDate, "yyyy-MM-dd");

    onFilter(filters);
  };

  const handleClearFilters = () => {
    setSellerId("");
    setMediaId("");
    setCreatedById("");
    setStartDate(undefined);
    setEndDate(undefined);
    onFilter({});
  };

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-lg p-4 border">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="space-y-2">
          <Label>Vendedor</Label>
          <Select value={sellerId} onValueChange={setSellerId}>
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {sellers.map((seller) => (
                <SelectItem key={seller.id} value={seller.id.toString()}>
                  {seller.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Mídia</Label>
          <Select value={mediaId} onValueChange={setMediaId}>
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {medias.map((media) => (
                <SelectItem key={media.id} value={media.id.toString()}>
                  {media.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Cadastrado por</Label>
          <Select value={createdById} onValueChange={setCreatedById}>
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {users.map((user) => (
                <SelectItem key={user.id} value={user.id.toString()}>
                  {user.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Data inicial</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !startDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecione"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={setStartDate}
                initialFocus
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>Data final</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !endDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecione"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={setEndDate}
                initialFocus
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button
          variant="outline"
          onClick={handleClearFilters}
          disabled={loading}
        >
          <FilterX className="h-4 w-4 mr-2" />
          Limpar filtros
        </Button>
        <Button onClick={handleFilter} disabled={loading}>
          <Search className="h-4 w-4 mr-2" />
          Filtrar
        </Button>
      </div>
    </div>
  );
};
